import { DriverDetailsFormItem } from '@acorn-portal/shared/src/components/manage-drivers/types';
import {
	MotoringConviction as FormConviction,
	MotoringClaim as FormMotoringClaim,
} from '@acorn-portal/shared/src/services/api';
import { MotoringClaim, MotoringConviction, PcDriver } from '@acorn-portal/shared/src/services/subscriptionAPI';
import {
	DriverFormValues,
	getDriverDefaultValues,
} from '@acorn-portal/shared/src/utils/initialValues/get-driver-form-values';
import { getFormDate, getInitialDrivingLicence } from '@acorn-portal/shared/src/utils/initialValues/utils';
import { getDriverShortFormValues } from './get-driver-short-form-values';

export const getMotoringConvictions = (motoringConvictions: MotoringConviction[]): FormConviction[] =>
	(motoringConvictions || []).map((conviction) => ({
		dateOfConviction: conviction.convictionDate ?? '',
		offenceCode: conviction.convictionCode ?? '',
		wasDisqualified: conviction.disqualified ?? false,
		numberOfPoints: conviction.points ?? 0,
		fineAmount: conviction.fine ?? 0,
		isPending: conviction.pending ?? false,
		dateOfOffence: conviction.offenceDate ?? '',
		banStartDate: conviction.banStartDate ?? '',
		banEndDate: conviction.banEndDate ?? '',
	}));

export const getMotoringClaims = (motoringClaims: MotoringClaim[]): FormMotoringClaim[] =>
	(motoringClaims || []).map((claim) => ({
		dateOfClaim: claim.incidentDate ?? '',
		typeOfClaim: claim.claimType ?? '',
		costOfClaim: claim.totalCosts ?? 0,
		atFault: claim.fault ?? false,
		noClaimsBonusAffected: claim.ncdAffected ?? false,
		statusOfClaim: claim.claimStatus ?? '',
	}));

/**
 * Generates values to edit the full driver form, specifically for the 30-day subscription journey,
 * typically used for editing additional driver information.
 *
 * @param driver - The driver data object containing occupation and employment details.
 * @returns The structured form values for editing additional driver information, or `null` if no data is provided.
 */
export const getDriverFormValues = (driver: PcDriver | null): DriverFormValues => {
	const defaultData = getDriverDefaultValues();

	if (!driver) return defaultData;
	const clonedDriver = structuredClone(driver);
	const employmentData = getDriverShortFormValues(clonedDriver);
	const formData: DriverFormValues = { ...defaultData, ...employmentData };

	formData.title = clonedDriver.title || '';
	formData.firstName = clonedDriver.firstName || '';
	formData.lastNames = clonedDriver.lastName || '';
	formData.dateOfBirth = getFormDate(clonedDriver.dateOfBirth);
	formData.maritalStatus = clonedDriver.maritalStatus || '';
	formData.relationshipToProposer = clonedDriver.relationshipToProposer || '';
	formData.isResidentFromBirth = clonedDriver.ukResidentSinceBirth ? 'yes' : 'no';
	formData.ukResidenceSince = clonedDriver.dateUkResidency ? getFormDate(clonedDriver.dateUkResidency) : undefined;
	formData.licenceType = clonedDriver.licenceType || '';
	formData.licenceObtained = getFormDate(clonedDriver.licencePassedDate);
	formData.licenceNumber = getInitialDrivingLicence({
		licenceNumber: clonedDriver.licenceNumber,
		title: clonedDriver.title,
		lastNames: clonedDriver.lastName,
		dateOfBirth: clonedDriver.dateOfBirth,
	} as DriverDetailsFormItem);
	formData.hasCriminalConvictions = clonedDriver.hasNonMotoringConvictions ? 'yes' : 'no';
	formData.hasBeenRefusedCover = clonedDriver.hasInsuranceRefused ? 'yes' : 'no';
	formData.accessToOtherVehicles = clonedDriver.accessToOtherVehicles ? 'yes' : 'no';
	formData.motoringConvictions = getMotoringConvictions(clonedDriver.motoringConvictions || []);
	formData.motoringClaims = getMotoringClaims(clonedDriver.motoringClaims || []);
	formData.hasMotoringConvictions = clonedDriver.motoringConvictions?.length || 0 > 0 ? 'yes' : 'no';
	formData.hasMotoringClaims = clonedDriver.motoringClaims?.length || 0 > 0 ? 'yes' : 'no';
	formData.driverUsage = clonedDriver.driverUsage || '';

	return formData;
};
